/**
 * Constants and utilities for Asian market rank qualification display
 * 
 * Markets: Philippines, Singapore, Malaysia, Brunei, Taiwan, Hong Kong
 * 
 * Requirements:
 * - Show actual volume for all legs
 * - Required legs display: numerator/denominator (e.g., 77/100)
 * - Non-required legs display: numerator only (e.g., 68)
 * - Highlight only required legs based on rank
 * - Active Legs Box shows Total/Required format
 */

// Asian markets that require the new rank qualification display logic
export const ASIAN_MARKETS = ["PH", "SG", "MY", "BN", "TW", "HK", "TH"] as const;

export type AsianMarketCode = typeof ASIAN_MARKETS[number];

// Rank codes to required legs mapping for ASIAN markets
export const RANK_LEG_REQUIREMENTS = {
  "Mgr": 0,           // Manager: No legs required
  "SrM": 1,           // Senior Manager: 1 leg required  
  "ExM": 2,           // Executive Manager: 2 legs required
  "Dir": 3,           // Director: 3 legs required
  "SrD": 3,           // Senior Director: 3 legs required
  "ExD": 3,           // Executive Director: 3 legs required
  "PrD": 3,           // Presidential Director: 3 legs required
  "PrS": 3,           // Presidential Sapphire: 3 legs required
  "PrR": 3,           // Presidential Ruby: 3 legs required,
  "DIA": 3,           // Diamond: 3 legs required,
  // Add more ranks as needed
} as const;

export const RANK_LEG_REQUIREMENTS_NON_ASIAN_MARKETS = {
  "Mgr": 3,           // Manager: 3 legs required
  "SrM": 3,           // Senior Manager: 3 legs required
  "ExM": 3,           // Executive Manager: 3 legs required
  "Dir": 3,           // Director: 3 legs required
  "SrD": 3,           // Senior Director: 3 legs required
  "ExD": 3,           // Executive Director: 3 legs required
  "PrD": 3,           // Presidential Director: 3 legs required
  "PrS": 3,           // Presidential Sapphire: 3 legs required
  "PrR": 3,           // Presidential Ruby: 3 legs required,
  "DIA": 3,           // Director: 3 legs required
} as const;

export type RankCode = keyof typeof RANK_LEG_REQUIREMENTS;

/**
 * Type definition for leg volumes used in calculations
 */
export interface LegVolumes {
  leg1?: number;
  leg2?: number;
  leg3?: number;
}

/**
 * Determines if a market is an Asian market requiring special rank qualification display
 * @param countryCode - The two-letter country code
 * @returns True if the market requires Asian-specific display logic
 */
export const isAsianMarket = (countryCode: string): boolean => {
  return ASIAN_MARKETS.includes(countryCode as AsianMarketCode);
};

/**
 * Gets the number of required legs for a given rank
 * @param rankCode - The rank code (e.g., 'Mgr', 'SrM', 'ExM')
 * @returns Number of legs required for the rank (0-3)
 */
export const getRequiredLegsForRank = (rankCode: string): number => {
  return RANK_LEG_REQUIREMENTS[rankCode as RankCode] ?? 0;
};

/**
 * Determines if a specific leg should be highlighted based on rank requirements
 * Legs are highlighted based on their volume (highest volume legs are highlighted first)
 * @param legNumber - The leg number (1, 2, or 3)
 * @param rankCode - The rank code
 * @param legVolumes - Object containing volumes for all legs
 * @returns True if the leg should be highlighted
 */
export const shouldHighlightLeg = (
  legNumber: number,
  rankCode: string,
  legVolumes: LegVolumes
): boolean => {
  const requiredLegs = getRequiredLegsForRank(rankCode);

  if (requiredLegs === 0) {
    return false; // No highlighting for Manager rank
  }

  // Sort legs by volume to determine which should be highlighted
  const sortedLegs = [
    { number: 1, volume: legVolumes.leg1 ?? 0 },
    { number: 2, volume: legVolumes.leg2 ?? 0 },
    { number: 3, volume: legVolumes.leg3 ?? 0 }
  ].sort((a, b) => b.volume - a.volume);

  // Get the leg numbers that should be highlighted (top N by volume)
  const legsToHighlight = sortedLegs.slice(0, requiredLegs).map(leg => leg.number);

  return legsToHighlight.includes(legNumber);
};

/**
 * Determines if a leg should show denominator based on rank requirements and leg priority
 * @param legNumber - The leg number (1, 2, or 3)
 * @param rankCode - The rank code
 * @param legVolumes - Object containing volumes for all legs
 * @returns True if the denominator should be shown
 */
export const shouldShowDenominator = (
  legNumber: number,
  rankCode: string,
  legVolumes: LegVolumes
): boolean => {
  return shouldHighlightLeg(legNumber, rankCode, legVolumes);
};

/**
 * Formats the Active Legs display for Asian markets
 * Returns format like "4/2" for Executive Manager or "4" for Manager
 * @param totalActiveLegs - Total number of active legs
 * @param rankCode - The rank code
 * @returns Formatted display string
 */
export const formatActiveLegsDisplay = (
  totalActiveLegs: number,
  rankCode: string
): string => {
  const requiredLegs = getRequiredLegsForRank(rankCode);

  if (requiredLegs === 0) {
    return totalActiveLegs.toString(); // Manager: show total only
  }

  return `${totalActiveLegs}/${requiredLegs}`; // Other ranks: show total/required
}; 